/**
 * AuthenticationBehavior ver10 module
 * @author Andrew D.Laptev <a.d.laptev@gmail.com>
 * @see https://www.onvif.org/ver10/authenticationbehavior/wsdl
 */

import { Onvif, OnvifServices } from './onvif';
import Service from './service';
import { ReferenceToken } from './interfaces/common';
import {
  AuthenticationPolicy,
  AuthenticationProfile,
  AuthenticationProfileInfo,
  Capabilities,
  CreateAuthenticationProfile,
  CreateAuthenticationProfileResponse,
  CreateSecurityLevel,
  CreateSecurityLevelResponse,
  DeleteAuthenticationProfile,
  DeleteSecurityLevel,
  GetAuthenticationProfileInfo,
  GetAuthenticationProfileInfoList,
  GetAuthenticationProfileInfoListResponse,
  GetAuthenticationProfileInfoResponse,
  GetAuthenticationProfileList,
  GetAuthenticationProfileListResponse,
  GetAuthenticationProfiles,
  GetAuthenticationProfilesResponse,
  GetSecurityLevelInfo,
  GetSecurityLevelInfoList,
  GetSecurityLevelInfoListResponse,
  GetSecurityLevelInfoResponse,
  GetSecurityLevelList,
  GetSecurityLevelListResponse,
  GetSecurityLevels,
  GetSecurityLevelsResponse,
  ModifyAuthenticationProfile,
  ModifySecurityLevel,
  RecognitionGroup,
  RecognitionMethod,
  SecurityLevel,
  SecurityLevelConstraint,
  SecurityLevelInfo,
  SetAuthenticationProfile,
  SetSecurityLevel,
} from './interfaces/authenticationbehavior';

/**
 * AuthenticationBehavior service
 * @example
 * ```ts
 * const profiles = await cam.authenticationBehavior.getAuthenticationProfileList();
 * const token = profiles.authenticationProfile![0].token;
 * const levels = await cam.authenticationBehavior.getSecurityLevelList();
 * ```
 */
export class AuthenticationBehavior extends Service {
  constructor(onvif: Onvif, service: keyof OnvifServices) {
    super(onvif, service);
  }

  private static tokensToBuild(tokens?: ReferenceToken[]) {
    if (!tokens?.length) {
      return undefined;
    }
    return tokens.length === 1 ? tokens[0] : tokens;
  }

  private static oneOrMany<T>(items: T[] | undefined, builder: (item: T) => unknown) {
    if (!items?.length) {
      return undefined;
    }
    const built = items.map((item) => builder(item));
    return built.length === 1 ? built[0] : built;
  }

  private static securityLevelConstraintToBuild(constraint: SecurityLevelConstraint) {
    return {
      ActiveRegularSchedule: constraint.activeRegularSchedule,
      ActiveSpecialDaySchedule: constraint.activeSpecialDaySchedule,
      ...(constraint.authenticationMode && { AuthenticationMode: constraint.authenticationMode }),
      SecurityLevelToken: constraint.securityLevelToken,
      ...(constraint.extension && { Extension: constraint.extension }),
    };
  }

  private static authenticationPolicyToBuild(policy: AuthenticationPolicy) {
    return {
      ScheduleToken: policy.scheduleToken,
      SecurityLevelConstraint: AuthenticationBehavior.oneOrMany(
        policy.securityLevelConstraint,
        AuthenticationBehavior.securityLevelConstraintToBuild,
      ),
      ...(policy.extension && { Extension: policy.extension }),
    };
  }

  private static recognitionMethodToBuild(method: RecognitionMethod) {
    return {
      RecognitionType: method.recognitionType,
      Order: method.order,
      ...(method.extension && { Extension: method.extension }),
    };
  }

  private static recognitionGroupToBuild(group: RecognitionGroup) {
    return {
      ...(group.recognitionMethod && {
        RecognitionMethod: AuthenticationBehavior.oneOrMany(
          group.recognitionMethod,
          AuthenticationBehavior.recognitionMethodToBuild,
        ),
      }),
      ...(group.extension && { Extension: group.extension }),
    };
  }

  private static authenticationProfileInfoToBuild(
    profile: AuthenticationProfileInfo | AuthenticationProfile,
  ) {
    return {
      $: { token: profile.token },
      Name: profile.name,
      ...(profile.description && { Description: profile.description }),
    };
  }

  private static authenticationProfileToBuild(profile: AuthenticationProfile) {
    return {
      ...AuthenticationBehavior.authenticationProfileInfoToBuild(profile),
      DefaultSecurityLevelToken: profile.defaultSecurityLevelToken,
      ...(profile.authenticationPolicy && {
        AuthenticationPolicy: AuthenticationBehavior.oneOrMany(
          profile.authenticationPolicy,
          AuthenticationBehavior.authenticationPolicyToBuild,
        ),
      }),
      ...(profile.extension && { Extension: profile.extension }),
    };
  }

  private static securityLevelInfoToBuild(level: SecurityLevelInfo | SecurityLevel) {
    return {
      $: { token: level.token },
      Name: level.name,
      Priority: level.priority,
      ...(level.description && { Description: level.description }),
    };
  }

  private static securityLevelToBuild(level: SecurityLevel) {
    return {
      ...AuthenticationBehavior.securityLevelInfoToBuild(level),
      ...(level.recognitionGroup && {
        RecognitionGroup: AuthenticationBehavior.oneOrMany(
          level.recognitionGroup,
          AuthenticationBehavior.recognitionGroupToBuild,
        ),
      }),
      ...(level.extension && { Extension: level.extension }),
    };
  }

  /**
   * Returns the capabilities of the authentication behavior service.
   */
  async getServiceCapabilities(): Promise<Capabilities> {
    const response = await this.request({
      GetServiceCapabilities: {},
    });
    return response.getServiceCapabilitiesResponse?.capabilities ?? {};
  }

  /**
   * Returns authentication profile info items for the requested tokens.
   * @param options
   */
  async getAuthenticationProfileInfo({
    token,
  }: GetAuthenticationProfileInfo): Promise<GetAuthenticationProfileInfoResponse> {
    const response = await this.request(
      {
        GetAuthenticationProfileInfo: {
          Token: AuthenticationBehavior.tokensToBuild(token),
        },
      },
      { array: ['authenticationProfileInfo'] },
    );
    return response.getAuthenticationProfileInfoResponse ?? {};
  }

  /**
   * Returns a list of authentication profile info items.
   * @param options
   */
  async getAuthenticationProfileInfoList(
    options: GetAuthenticationProfileInfoList = {},
  ): Promise<GetAuthenticationProfileInfoListResponse> {
    const response = await this.request(
      {
        GetAuthenticationProfileInfoList: {
          ...(options.limit !== undefined && { Limit: options.limit }),
          ...(options.startReference && { StartReference: options.startReference }),
        },
      },
      { array: ['authenticationProfileInfo'] },
    );
    return response.getAuthenticationProfileInfoListResponse ?? {};
  }

  /**
   * Returns authentication profile items for the requested tokens.
   * @param options
   */
  async getAuthenticationProfiles({
    token,
  }: GetAuthenticationProfiles): Promise<GetAuthenticationProfilesResponse> {
    const response = await this.request(
      {
        GetAuthenticationProfiles: {
          Token: AuthenticationBehavior.tokensToBuild(token),
        },
      },
      { array: ['authenticationProfile'] },
    );
    return response.getAuthenticationProfilesResponse ?? {};
  }

  /**
   * Returns a list of authentication profile items.
   * @param options
   */
  async getAuthenticationProfileList(
    options: GetAuthenticationProfileList = {},
  ): Promise<GetAuthenticationProfileListResponse> {
    const response = await this.request(
      {
        GetAuthenticationProfileList: {
          ...(options.limit !== undefined && { Limit: options.limit }),
          ...(options.startReference && { StartReference: options.startReference }),
        },
      },
      { array: ['authenticationProfile'] },
    );
    return response.getAuthenticationProfileListResponse ?? {};
  }

  /**
   * Creates a new authentication profile.
   * @param options
   */
  async createAuthenticationProfile({
    authenticationProfile,
  }: CreateAuthenticationProfile): Promise<CreateAuthenticationProfileResponse['token']> {
    const response = await this.request({
      CreateAuthenticationProfile: {
        AuthenticationProfile: AuthenticationBehavior.authenticationProfileToBuild(authenticationProfile),
      },
    });
    return response.createAuthenticationProfileResponse.token;
  }

  /**
   * Creates or replaces an authentication profile (requires ClientSuppliedTokenSupported).
   * @param options
   */
  async setAuthenticationProfile({ authenticationProfile }: SetAuthenticationProfile): Promise<void> {
    await this.request({
      SetAuthenticationProfile: {
        AuthenticationProfile: AuthenticationBehavior.authenticationProfileToBuild(authenticationProfile),
      },
    });
  }

  /**
   * Modifies an existing authentication profile.
   * @param options
   */
  async modifyAuthenticationProfile({ authenticationProfile }: ModifyAuthenticationProfile): Promise<void> {
    await this.request({
      ModifyAuthenticationProfile: {
        AuthenticationProfile: AuthenticationBehavior.authenticationProfileToBuild(authenticationProfile),
      },
    });
  }

  /**
   * Deletes an authentication profile.
   * @param options
   */
  async deleteAuthenticationProfile({ token }: DeleteAuthenticationProfile): Promise<void> {
    await this.request({
      DeleteAuthenticationProfile: {
        Token: token,
      },
    });
  }

  /**
   * Returns security level info items for the requested tokens.
   * @param options
   */
  async getSecurityLevelInfo({ token }: GetSecurityLevelInfo): Promise<GetSecurityLevelInfoResponse> {
    const response = await this.request(
      {
        GetSecurityLevelInfo: {
          Token: AuthenticationBehavior.tokensToBuild(token),
        },
      },
      { array: ['securityLevelInfo'] },
    );
    return response.getSecurityLevelInfoResponse ?? {};
  }

  /**
   * Returns a list of security level info items.
   * @param options
   */
  async getSecurityLevelInfoList(
    options: GetSecurityLevelInfoList = {},
  ): Promise<GetSecurityLevelInfoListResponse> {
    const response = await this.request(
      {
        GetSecurityLevelInfoList: {
          ...(options.limit !== undefined && { Limit: options.limit }),
          ...(options.startReference && { StartReference: options.startReference }),
        },
      },
      { array: ['securityLevelInfo'] },
    );
    return response.getSecurityLevelInfoListResponse ?? {};
  }

  /**
   * Returns security level items for the requested tokens.
   * @param options
   */
  async getSecurityLevels({ token }: GetSecurityLevels): Promise<GetSecurityLevelsResponse> {
    const response = await this.request(
      {
        GetSecurityLevels: {
          Token: AuthenticationBehavior.tokensToBuild(token),
        },
      },
      { array: ['securityLevel'] },
    );
    return response.getSecurityLevelsResponse ?? {};
  }

  /**
   * Returns a list of security level items.
   * @param options
   */
  async getSecurityLevelList(options: GetSecurityLevelList = {}): Promise<GetSecurityLevelListResponse> {
    const response = await this.request(
      {
        GetSecurityLevelList: {
          ...(options.limit !== undefined && { Limit: options.limit }),
          ...(options.startReference && { StartReference: options.startReference }),
        },
      },
      { array: ['securityLevel'] },
    );
    return response.getSecurityLevelListResponse ?? {};
  }

  /**
   * Creates a new security level.
   * @param options
   */
  async createSecurityLevel({ securityLevel }: CreateSecurityLevel): Promise<CreateSecurityLevelResponse['token']> {
    const response = await this.request({
      CreateSecurityLevel: {
        SecurityLevel: AuthenticationBehavior.securityLevelToBuild(securityLevel),
      },
    });
    return response.createSecurityLevelResponse.token;
  }

  /**
   * Creates or replaces a security level (requires ClientSuppliedTokenSupported).
   * @param options
   */
  async setSecurityLevel({ securityLevel }: SetSecurityLevel): Promise<void> {
    await this.request({
      SetSecurityLevel: {
        SecurityLevel: AuthenticationBehavior.securityLevelToBuild(securityLevel),
      },
    });
  }

  /**
   * Modifies an existing security level.
   * @param options
   */
  async modifySecurityLevel({ securityLevel }: ModifySecurityLevel): Promise<void> {
    await this.request({
      ModifySecurityLevel: {
        SecurityLevel: AuthenticationBehavior.securityLevelToBuild(securityLevel),
      },
    });
  }

  /**
   * Deletes a security level.
   * @param options
   */
  async deleteSecurityLevel({ token }: DeleteSecurityLevel): Promise<void> {
    await this.request({
      DeleteSecurityLevel: {
        Token: token,
      },
    });
  }
}
